import { z } from 'zod';
import { makeStandardContactSchema } from '../contact';
import {
  makeStandardFreightBaseSchema,
  makeStandardOriginatorSchema,
  type StandardFreightBase,
} from '../freightOrder';
import { ResponsiblePartyRole } from '../responsibleParty';
import {
  makeStandardContractedMovementSchema,
  type VoomaContractedMovement,
} from './contractedMovement';

const makeOrderContactSchema = () =>
  z.object({
    name: z.string().optional(),
    email: z.string().optional(),
    phone: z.string().optional(),
    department_id: z.string().optional(),
    userId: z.string().optional(),
    role: z.nativeEnum(ResponsiblePartyRole).optional(),
  });

// NOTE(jonty): orderId and originator are optional because imported orders
// omit them, while standard orders always have both.
const makeVoomaShipmentOrderSchema = () =>
  makeStandardFreightBaseSchema()
    .omit({ orderId: true })
    .extend({
      orderId: z.string().optional(),
      originator: makeStandardOriginatorSchema()
        .extend({
          status: z.enum(['TEST', 'PRODUCTION']).optional(),
          contact: makeStandardContactSchema().optional(),
        })
        .optional(),
      responsibleParties: z.array(makeOrderContactSchema()).optional(),
      loadboard: z.boolean().optional(),
      loadboardPoster: z.string().optional(),
      loadboardRate: z.union([z.string(), z.number()]).optional(),
      departmentAllocation: z.string().optional(),
    });

export const makeVoomaShipmentSchema = () =>
  z.object({
    id: z.string().optional(),
    user: z.object({ id: z.string() }).optional(),
    movements: z.array(makeStandardContractedMovementSchema()),
    orders: z.array(makeVoomaShipmentOrderSchema()),
    // NOTE(max): Some systems present a different ID to end users than the one
    // used for API calls and deep linking (e.g. Turvo ID vs custom ID).
    externalId: z.string().optional(),
    externalDisplayId: z.string().optional(),
    externalUpdatedAt: z.string().optional(),
    tags: z.array(z.string()).optional(),
  });

const voomaShipmentSchema = makeVoomaShipmentSchema();
type VoomaShipmentFromSchema = z.infer<typeof voomaShipmentSchema>;

type VoomaShipmentOrder = Omit<
  VoomaShipmentFromSchema['orders'][number],
  'stops'
> & {
  stops: StandardFreightBase['stops'];
};

export type VoomaShipment = Omit<
  VoomaShipmentFromSchema,
  'movements' | 'orders'
> & {
  movements: VoomaContractedMovement[];
  orders: VoomaShipmentOrder[];
};
